import type { MassSystem } from './MassSystem';
import type { BrainQuality, BrainSystem } from './BrainSystem';
import type { RelationResolver } from './RelationResolver';
import type { AbilitySystem } from './AbilitySystem';
import type { Entity } from '@/types';

export type ConsumeEventType = 'start' | 'progress' | 'complete' | 'interrupted' | 'rejected';

export interface ConsumeEvent {
  type: ConsumeEventType;
  predator: Entity;
  prey: Entity;
  progress: number;
  biomassGained?: number;
  brainGained?: { quality: BrainQuality; mass: number };
}

export interface ConsumptionConfig {
  brainQualityFor: (speciesId: string) => BrainQuality;
  consumeDurationMs: (targetMass: number) => number;
  /** Maximum concurrent consumes per predator. Defaults to 1. Evaluated each contact so tech-tree upgrades can flip it live. */
  maxConcurrent?: () => number;
  /** Multiplier applied to base consume duration. Defaults to 1.0. Sub-1 = faster. Evaluated each contact. */
  durationMultiplier?: () => number;
}

interface ActiveConsume {
  prey: Entity;
  elapsedMs: number;
  durationMs: number;
}

export type ConsumeListener = (event: ConsumeEvent) => void;

export class ConsumptionController {
  private readonly active = new Map<string, ActiveConsume>();
  private listeners: ConsumeListener[] = [];

  constructor(
    private readonly mass: MassSystem,
    private readonly brain: BrainSystem,
    private readonly relations: RelationResolver,
    private readonly abilities: AbilitySystem,
    private readonly config: ConsumptionConfig,
  ) {}

  attemptContact(
    predator: Entity,
    prey: Entity,
  ): 'started' | 'already' | 'rejected' | 'lethal' | 'at-capacity' {
    const rel = this.relations.resolve(predator, prey);
    if (rel === 'PREDATOR') {
      this.emit({ type: 'rejected', predator, prey, progress: 0 });
      return 'lethal';
    }
    if (rel === 'NEUTRAL') {
      this.emit({ type: 'rejected', predator, prey, progress: 0 });
      return 'rejected';
    }
    const key = this.keyFor(predator.id, prey.id);
    if (this.active.has(key)) return 'already';

    const cap = this.config.maxConcurrent?.() ?? 1;
    const activeForPredator = this.countActiveFor(predator.id);
    if (activeForPredator >= cap) {
      this.emit({ type: 'rejected', predator, prey, progress: 0 });
      return 'at-capacity';
    }

    const multiplier = this.config.durationMultiplier?.() ?? 1;
    const baseDuration = this.config.consumeDurationMs(prey.mass);
    const durationMs = Math.max(0, baseDuration * multiplier);
    this.active.set(key, { prey, elapsedMs: 0, durationMs });
    this.emit({ type: 'start', predator, prey, progress: 0 });
    return 'started';
  }

  private countActiveFor(predatorId: string): number {
    let n = 0;
    for (const key of this.active.keys()) {
      if (this.parseKey(key).predatorId === predatorId) n++;
    }
    return n;
  }

  tick(predator: Entity, dtMs: number, stillInContact: (preyId: string) => boolean): void {
    const toComplete: Array<{ key: string; prey: Entity }> = [];
    const toInterrupt: Array<{ key: string; prey: Entity; progress: number }> = [];

    for (const [key, state] of this.active) {
      const { predatorId, preyId } = this.parseKey(key);
      if (predatorId !== predator.id) continue;

      if (!stillInContact(preyId)) {
        toInterrupt.push({
          key,
          prey: state.prey,
          progress: state.durationMs > 0 ? state.elapsedMs / state.durationMs : 1,
        });
        continue;
      }

      state.elapsedMs += dtMs;
      const progress =
        state.durationMs > 0 ? Math.min(1, state.elapsedMs / state.durationMs) : 1;
      this.emit({ type: 'progress', predator, prey: state.prey, progress });

      if (progress >= 1) {
        toComplete.push({ key, prey: state.prey });
      }
    }

    for (const entry of toInterrupt) {
      this.active.delete(entry.key);
      this.emit({
        type: 'interrupted',
        predator,
        prey: entry.prey,
        progress: entry.progress,
      });
    }

    for (const entry of toComplete) {
      this.active.delete(entry.key);
      this.complete(predator, entry.prey);
    }
  }

  cancelAllFor(predatorId: string): void {
    for (const [key, state] of Array.from(this.active)) {
      const { predatorId: pid } = this.parseKey(key);
      if (pid !== predatorId) continue;
      this.active.delete(key);
      this.emit({
        type: 'interrupted',
        predator: { id: predatorId, speciesId: 'unknown', mass: 0 },
        prey: state.prey,
        progress: state.durationMs > 0 ? state.elapsedMs / state.durationMs : 0,
      });
    }
  }

  isActive(predatorId: string, preyId: string): boolean {
    return this.active.has(this.keyFor(predatorId, preyId));
  }

  onEvent(listener: ConsumeListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private complete(predator: Entity, prey: Entity): void {
    const quality = this.config.brainQualityFor(prey.speciesId);

    // Size-absorption penalty: a small blob cannot fully absorb a
    // large meal. Full absorption at parity or above; a sqrt curve
    // softens the loss below parity so big finds are still worthwhile.
    const ratio = predator.mass / prey.mass;
    const absorbed =
      ratio >= 1 ? prey.mass : Math.max(prey.mass * 0.1, prey.mass * Math.sqrt(ratio));

    this.mass.add(absorbed);
    this.brain.consume(quality, absorbed);
    this.abilities.recordKill(prey.speciesId);
    this.emit({
      type: 'complete',
      predator,
      prey,
      progress: 1,
      biomassGained: absorbed,
      brainGained: { quality, mass: absorbed },
    });
  }

  private emit(event: ConsumeEvent): void {
    for (const listener of this.listeners) {
      listener(event);
    }
  }

  private keyFor(predatorId: string, preyId: string): string {
    return `${predatorId}::${preyId}`;
  }

  private parseKey(key: string): { predatorId: string; preyId: string } {
    const [predatorId, preyId] = key.split('::');
    return { predatorId, preyId };
  }
}
